(() => {
  const MAX_ITEMS = 20;
  const MAX_CSV_LENGTH = 8192;
  const VARIANT_ID_PATTERN = /^\d+$/;
  const HANDLE_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

  const Csv = {
    normalize(csv) {
      return this.toPairs(this.parsePairs(csv));
    },
    parsePairs(csv) {
      const map = new Map();
      if (!csv) return map;
      const rawCsv = String(csv);
      if (rawCsv.length > MAX_CSV_LENGTH) return map;
      const boundedCsv = rawCsv;
      for (const token of boundedCsv.split(",")) {
        const t = token.trim();
        if (!t) continue;
        const parts = t.split("|");
        if (parts.length !== 2) continue;
        const [vidRaw, handleRaw] = parts;
        const vidText = String(vidRaw).trim();
        if (!VARIANT_ID_PATTERN.test(vidText)) continue;
        const vid = Number(vidText);
        const handle = String(handleRaw || "").trim();
        if (!Number.isSafeInteger(vid) || vid <= 0 || !HANDLE_PATTERN.test(handle)) continue;
        const key = `${vid}|${handle}`;
        if (map.has(key)) continue;
        map.set(key, { vid, handle });
        if (map.size >= MAX_ITEMS) break;
      }
      return map;
    },
    toPairs(map) {
      return Array.from(map.values())
        .map((x) => `${x.vid}|${x.handle}`)
        .join(",");
    },
    toVids(map) {
      return Array.from(map.values())
        .map((x) => x.vid)
        .join(",");
    },
  };

  const Events = {
    CHANGE: "product-lists:change",
    types: Object.freeze({
      init: "init",
      sync: "sync",
      add: "add",
      remove: "remove",
      clear: "clear",
      importAppend: "import:append",
      importReplace: "import:replace",
      restore: "restore:ls",
      noAdd: "nochange:add",
      noRemove: "nochange:remove",
      noClear: "nochange:clear",
      replaceCsv: "replace:csv",
      removeGroup: "remove:group",
    }),
    emit(source, listName, core) {
      const list = core.lists[listName];
      const detail = {
        type: source,
        list: listName,
        pairs: Array.from(list.items.values()),
        csv: list.csvPairs(),
        vids: list.vids(),
      };
      document.dispatchEvent(new CustomEvent(Events.CHANGE, {
        detail
      }));
      for (const cb of core._subs) {
        try {
          cb(detail);
        } catch {}
      }
    },
    broadcast(source, core) {
      Events.emit(source, "compare", core);
      Events.emit(source, "favorites", core);
    },
  };

  const CartService = {
    async fetchCart() {
      const res = await fetch("/cart.js", {
        credentials: "same-origin"
      });
      if (!res.ok) throw new Error("Cart fetch failed");
      return res.json();
    },
    async updateAttributesJSON(attrs) {
      const payload = {
        attributes: {
          ...attrs
        }
      };
      const res = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "same-origin",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Cart update failed");
      return res.json();
    },
  };

  const UrlService = {
    params() {
      try {
        return new URL(location.href).searchParams;
      } catch {
        return new URLSearchParams();
      }
    },
    hasPublicParam(list) {
      const p = UrlService.params();
      if (list === "compare") return p.has("compare");
      if (list === "favorites") return p.has("favorites");
      return p.has("compare") || p.has("favorites");
    },
    getLegacyCsv(list) {
      const p = UrlService.params();
      const key = list === "favorites" ? "favorites" : "compare";
      return String(p.get(key) || "").trim();
    },
    stripPublicParam(list) {
      try {
        const url = new URL(location.href);
        if (list === "compare") url.searchParams.delete("compare");
        if (list === "favorites") url.searchParams.delete("favorites");
        history.replaceState(null, "", url.toString());
      } catch {}
    },
    stripAllQuery() {
      try {
        const url = new URL(location.href);
        url.search = "";
        history.replaceState(null, "", url.toString());
      } catch {}
    },
    buildShareURL({
      compareCSV,
      favoritesCSV
    }) {
      const url = new URL(location.href);
      if (compareCSV) url.searchParams.set("compare", compareCSV);
      else url.searchParams.delete("compare");
      if (favoritesCSV) url.searchParams.set("favorites", favoritesCSV);
      else url.searchParams.delete("favorites");
      return url.toString();
    },
  };

  window.ProductListsServices = {
    Csv,
    Events,
    CartService,
    UrlService
  };

  const ATTR = {
    compare: {
      pairs: "_compare_pairs",
      vids: "_compare_vids",
      payload: "_compare_payload"
    },
    favorites: {
      pairs: "_favorites_pairs",
      vids: "_favorites_vids",
      payload: "_favorites_payload"
    },
  };

  const POLL_TIMEOUT_MS = 3500;
  const POLL_INTERVAL_MS = 150;

  class ListModel {
    constructor(name, attr) {
      this.name = name;
      this.attr = attr;
      this.items = new Map();
    }
    loadFromCart(cart) {
      const csv = cart?.attributes?.[this.attr.pairs] || "";
      this.items = Csv.parsePairs(csv);
    }
    has(vid, handle) {
      return this.items.has(`${vid}|${handle}`);
    }
    count() {
      return this.items.size;
    }
    vids() {
      return Array.from(this.items.values()).map((x) => x.vid);
    }
    csvPairs() {
      return Csv.toPairs(this.items);
    }
    csvVids() {
      return Csv.toVids(this.items);
    }
    add(vid, handle) {
      if (this.items.size >= MAX_ITEMS) return false;
      const vidText = String(vid).trim();
      const canonicalHandle = String(handle || '').trim();
      if (!VARIANT_ID_PATTERN.test(vidText) || !HANDLE_PATTERN.test(canonicalHandle)) return false;
      const canonicalVid = Number(vidText);
      if (!Number.isSafeInteger(canonicalVid) || canonicalVid <= 0) return false;
      const key = `${canonicalVid}|${canonicalHandle}`;
      if (this.items.has(key)) return false;
      this.items.set(key, {
        vid: canonicalVid,
        handle: canonicalHandle
      });
      return true;
    }
    remove(vid, handle) {
      return this.items.delete(`${vid}|${handle}`);
    }
    clear() {
      this.items.clear();
    }
    replaceFromCSV(csv) {
      this.items = Csv.parsePairs(Csv.normalize(csv));
    }
    removeMany(predicate) {
      let changed = false;
      for (const [key, val] of Array.from(this.items.entries())) {
        if (predicate(val)) {
          this.items.delete(key);
          changed = true;
        }
      }
      return changed;
    }
    mergeAppendFromMap(map) {
      const out = new Map(this.items);
      for (const [key, v] of map) {
        if (out.size >= MAX_ITEMS) break;
        out.set(key, v);
      }
      this.items = out;
    }
  }

  class Core {
    constructor() {
      this.lists = {
        compare: new ListModel("compare", ATTR.compare),
        favorites: new ListModel("favorites", ATTR.favorites),
      };
      this._subs = new Set();
      this.ready = this.init();
    }

    async init() {
      const cart = await CartService.fetchCart();
      this.lists.compare.loadFromCart(cart);
      this.lists.favorites.loadFromCart(cart);
      Events.broadcast(Events.types.init, this);
    }

    onChange(cb) {
      if (typeof cb === "function") this._subs.add(cb);
    }
    offChange(cb) {
      this._subs.delete(cb);
    }

    async writeToCartAndSync() {
      const attrs = {};
      const now = Date.now();
      for (const k of ["compare", "favorites"]) {
        const list = this.lists[k];
        const pairs = list.csvPairs();
        const vids = list.csvVids();
        attrs[ATTR[k].pairs] = pairs;
        attrs[ATTR[k].vids] = vids;
        attrs[ATTR[k].payload] = JSON.stringify({
          pairs,
          vids,
          ts: now,
          v: 322
        });
      }
      await CartService.updateAttributesJSON(attrs);

      const t0 = Date.now();
      while (Date.now() - t0 < POLL_TIMEOUT_MS) {
        try {
          const cart = await CartService.fetchCart();
          const cNow = cart?.attributes?.[ATTR.compare.pairs] || "";
          const wNow = cart?.attributes?.[ATTR.favorites.pairs] || "";
          if (cNow === attrs[ATTR.compare.pairs] && wNow === attrs[ATTR.favorites.pairs]) break;
        } catch {}
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      }

      Events.broadcast(Events.types.sync, this);
    }

    async add(list, vid, handle) {
      await this.ready;
      const model = this.lists[list];
      if (!model) return false;
      const changed = model.add(vid, handle);
      if (!changed) {
        Events.emit(Events.types.noAdd, list, this);
        return false;
      }
      await this.writeToCartAndSync();
      Events.emit(Events.types.add, list, this);
      return true;
    }

    async remove(list, vid, handle, {
      confirmPrompt
    } = {}) {
      await this.ready;
      const model = this.lists[list];
      if (!model) return false;
      const changed = model.remove(vid, handle);
      if (!changed) {
        Events.emit(Events.types.noRemove, list, this);
        return false;
      }
      await this.writeToCartAndSync();
      Events.emit(Events.types.remove, list, this);
      return true;
    }

    async removeMany(list, predicate, {
      confirmPrompt
    } = {}) {
      await this.ready;
      const model = this.lists[list];
      if (!model) return false;
      const changed = model.removeMany(predicate);
      if (!changed) {
        Events.emit(Events.types.noRemove, list, this);
        return false;
      }
      await this.writeToCartAndSync();
      Events.emit(Events.types.removeGroup, list, this);
      return true;
    }

    async clear(list, {
      confirmPrompt
    } = {}) {
      await this.ready;
      const model = this.lists[list];
      if (!model) return false;
      if (!model.count()) {
        Events.emit(Events.types.noClear, list, this);
        return false;
      }
      model.clear();
      await this.writeToCartAndSync();
      Events.emit(Events.types.clear, list, this);
      return true;
    }

    has(list, vid, handle) {
      return this.lists[list]?.has(vid, handle) || false;
    }
    count(list) {
      return this.lists[list]?.count() || 0;
    }
    getVids(list) {
      return this.lists[list]?.vids() || [];
    }
    encodePairsCSV(list) {
      return this.lists[list]?.csvPairs() || "";
    }

    replaceFromCSV(list, csv) {
      const model = this.lists[list];
      if (!model) return false;
      model.replaceFromCSV(csv);
      Events.emit(Events.types.replaceCsv, list, this);
      return true;
    }

    getShareURL(list) {
      if (list === "compare") {
        return UrlService.buildShareURL({
          compareCSV: this.encodePairsCSV("compare"),
          favoritesCSV: "",
        });
      }
      if (list === "favorites") {
        return UrlService.buildShareURL({
          compareCSV: "",
          favoritesCSV: this.encodePairsCSV("favorites"),
        });
      }
      return UrlService.buildShareURL({
        compareCSV: this.encodePairsCSV("compare"),
        favoritesCSV: this.encodePairsCSV("favorites"),
      });
    }

    async importFromURL(list, {
      append = true
    } = {}) {
      await this.ready;
      const legacyCsv = UrlService.getLegacyCsv(list);
      if (!legacyCsv) return {
        added: 0
      };

      const incoming = Csv.parsePairs(legacyCsv);
      if (append) {
        const before = this.lists[list].count();
        this.lists[list].mergeAppendFromMap(incoming);
        const added = this.lists[list].count() - before;
        if (added > 0) {
          await this.writeToCartAndSync();
          Events.emit(Events.types.importAppend, list, this);
        }
        return {
          added
        };
      } else {
        this.lists[list].items = incoming;
        await this.writeToCartAndSync();
        Events.emit(Events.types.importReplace, list, this);
        return {
          added: this.lists[list].count()
        };
      }
    }

    static stripShareParamFromLocation(list) {
      UrlService.stripPublicParam(list);
    }
    static stripAllQueryFromLocation() {
      UrlService.stripAllQuery();
    }

    async flush() {
      await this.writeToCartAndSync();
    }
  }

  if (!window.ProductLists) window.ProductLists = new Core();
})();
